import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import { styled } from '@mui/material/styles';
import { CanceledError } from 'axios';
import Loader from "common/components/loader";
import { OrderDirection } from 'common/contracts/order-direction';
import PaginationParameters from 'common/contracts/pagination-parameters';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useSession } from 'users/session/session-context';
import FeeScheduleTemplateAPIService from 'work/entities/fee-schedule-template/api/fee-schedule-template-api-service';
import FeeScheduleTemplateQuery, { FeeScheduleTemplateFilterParameters, FeeScheduleTemplateOrderField, FeeScheduleTemplateOrderParameters } from 'work/entities/fee-schedule-template/api/request-contracts/query-fee-schedule-template-api-request';
import FeeScheduleCategory, {
  FeeScheduleCategoryMaskedCurrency
} from 'work/values/fee-schedule-category/fee-schedule-category';

const ListContainer = styled(List)(({ theme }) => ({
  maxHeight: theme.spacing(50),
  maxWidth: theme.spacing(50)
}));

type FeeScheduleExistingCategoriesProps = {
  categories?: Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>;
  disabledCategories?: Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>;
  onCategoriesSelected: (categories: FeeScheduleCategory[]) => void
};

export default function FeeScheduleExistingCategories(props: Readonly<FeeScheduleExistingCategoriesProps>) {
  const {
    categories,
    disabledCategories,
    onCategoriesSelected
  } = props;

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [existingCategories, setExistingCategories] = React.useState<FeeScheduleCategory[]>([]);
  const [existingMaskedCategories, setExistingMaskedCategories] = React.useState<FeeScheduleCategoryMaskedCurrency[]>([]);

  const [selectedCategories, setSelectedCategories] = React.useState<FeeScheduleCategory[]>([]);
  const [paginatedParameters, setPaginatedParameters] = React.useState<PaginationParameters>();

  const session = useSession();

  useEffect(() => {
    let abortController = new AbortController();
    if (categories?.every(cat => 'fee' in cat)) {
      setExistingMaskedCategories(categories);
      return;
    }
    if (categories?.every(cat => cat instanceof FeeScheduleCategory)) {
      setExistingCategories(categories as FeeScheduleCategory[]);
    } else if (categories) {
      setExistingMaskedCategories(categories as FeeScheduleCategoryMaskedCurrency[]);
    } else {
      void getExistingCategories(abortController);
      return () => {
        abortController.abort();
        abortController = new AbortController();
      };
    }
  }, []);

  async function getExistingCategories(abortController: AbortController) {
    const entityId = session?.currentEntity.id;
    if (!entityId) return;

    setIsLoading(true);
    try {
      const filterParams = new FeeScheduleTemplateFilterParameters(entityId);

      const orderParameters = new FeeScheduleTemplateOrderParameters(
        FeeScheduleTemplateOrderField.Name,
        OrderDirection.Descending
      );

      const templateQuery: FeeScheduleTemplateQuery = new FeeScheduleTemplateQuery(
        filterParams,
        orderParameters,
        paginatedParameters ?? new PaginationParameters(0, 100)
      );

      const feeScheduleService = new FeeScheduleTemplateAPIService(session);
      const templates =
        await feeScheduleService.getFeeScheduleTemplates(
          templateQuery,
          abortController
        );
      templates.data.forEach((template) => {
        template.categories.forEach(category => {
          if (category instanceof FeeScheduleCategoryMaskedCurrency) {
            existingMaskedCategories.push(category);
          } else {
            existingCategories.push(category);
          }
        });
      });
    }
    catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      enqueueSnackbar(
        "Failed to load existing fee schedule templates.",
        {
          variant: "error",
        }
      );
    } finally {
      setPaginatedParameters((prev) => {
        if (prev) {
          return new PaginationParameters(prev.pageIndex, prev.pageSize);
        }
      });
      setIsLoading(false);
    }
  }

  const isFormValid = () => selectedCategories?.length > 0;

  function handleSelectClicked(category: FeeScheduleCategory) {
    const selected = [...selectedCategories];
    const index = selected.indexOf(category);
    if (index > -1) {
      selected.splice(index, 1);
    }
    else {
      selected.push(category);
    }
    setSelectedCategories(selected);
  }

  function isSelected(category: FeeScheduleCategory) {
    return selectedCategories?.map(c => c.name).includes(category.name);
  }

  function handleAddSelectedClicked() {
    onCategoriesSelected?.(selectedCategories);
  }

  return (
    <div>
      <ListContainer>
        {isLoading && <Loader />}
        {!isLoading && (
          <>
            {(!existingCategories || existingCategories.length < 1) && (
              <Typography>No existing fee schedule categories</Typography>
            )}
            {existingCategories.map(category => {
              return (
                <ListItemButton
                  key={category.name.value}
                  alignItems="flex-start"
                  disableGutters
                  disabled={disabledCategories?.map(c => c.name?.value).includes(category.name.value)}
                  onClick={() => handleSelectClicked(category)}
                >
                  <ListItemIcon>
                    <Checkbox
                      color="primary"
                      checked={isSelected(category) || disabledCategories?.map(c => c.name.value).includes(category.name.value)}
                      value={category}
                    />
                  </ListItemIcon>
                  <ListItemText
                    style={{ whiteSpace: 'pre-line' }}
                    primary={category.name.value}
                    secondary={category.feeString}
                  />
                </ListItemButton>
              );
            })}
          </>
        )}
      </ListContainer>
      <Button
        color="primary"
        variant="contained"
        disabled={!isFormValid()}
        onClick={handleAddSelectedClicked}>
        Add Selected
      </Button>
    </div>
  )
}
